/**
 * Registry of named themes the UI can switch between. Adding a new theme is
 * an additive change: append an entry here AND drop a `.theme-<id>` /
 * `.dark.theme-<id>` CSS block in `globals.css`. No other file needs to
 * know.
 *
 * `default` is a sentinel — it resolves to the project's stock shadcn
 * tokens at `:root` / `.dark`, so a fresh install with nothing in
 * `localStorage` still renders correctly. Other themes layer on top by
 * matching `<html class="dark theme-<id>">`.
 */

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedMode = 'light' | 'dark';
export type ThemeId = string;

export interface ThemeRegistryEntry {
  id: ThemeId;
  label: string;
  /** `preview` themes render but are flagged in the picker. */
  status?: 'stable' | 'preview';
}

export const DEFAULT_THEME_ID = 'default';

export const THEME_REGISTRY: readonly ThemeRegistryEntry[] = [
  { id: DEFAULT_THEME_ID, label: 'Default', status: 'stable' },
  // Empty scaffold — `.theme-cotality` / `.dark.theme-cotality` blocks in
  // `globals.css` are placeholder selectors with no overrides yet. Listed
  // here so the picker proves the additive substrate works structurally;
  // brand HSL values land in a follow-up once the brand guide is sourced
  // (called out in the PR description so it isn't a merge-time surprise).
  { id: 'cotality', label: 'Cotality', status: 'preview' },
];

export function isRegisteredTheme(id: string): boolean {
  return THEME_REGISTRY.some((t) => t.id === id);
}

export function resolveThemeId(rawId: string | null): ThemeId {
  if (rawId === null) return DEFAULT_THEME_ID;
  return isRegisteredTheme(rawId) ? rawId : DEFAULT_THEME_ID;
}
